import { useEffect, useState } from "react";
import { Col, Row, Table } from "react-bootstrap";
import useTranslator from "../../../../hooks/useTranslator";
import CommonModal from "../../../../components/shared/modalLayout/CommonModal";
import InputWithLabel from "../../../../components/shared/fields/InputWithLabel";
import Button from "../../../../components/shared/buttons/button";
import {
  useCreateGasConsumptionMutation,
  useUpdateGasConsumptionMutation,
} from "./gasConsumptionApi";
import moment from "moment";
import { convertToUserTimezone, getCurrentDate } from "../../../../helpers/Utils";

const FC = 9.1107;
const PRICE_PER_L = 11.2;

const GasConsumptionModal = ({ show, setShow, record, mode }) => {
  const { _t } = useTranslator();
  
  const getInitialOvenData = () => ({
    initial_reading: "",
    final_reading: "",
    m3_consumed: "",
    liters_consumed: "",
    total_cost: "",
  });

  const getInitialFormData = () => ({
    entered_date_time: getCurrentDate("YYYY-MM-DDTHH:mm"),
    fc: FC,
    price_per_l: PRICE_PER_L,
    ovens: Array(7).fill(null).map(() => getInitialOvenData()),    
  });

  const [formData, setFormData] = useState(getInitialFormData());

  // RTK Query
  const [createGasConsumptionRecord, { isLoading: isCreating }] =
    useCreateGasConsumptionMutation();
  const [updateGasConsumptionRecord, { isLoading: isUpdating }] =
    useUpdateGasConsumptionMutation();

  useEffect(() => {
    if (show) {
      if (record && mode === "edit") {
        // Map the ovens array from backend to match our form structure
        const mappedOvens = Array(7).fill(null).map((_, index) => {
          const ovenData = record.ovens?.find(o => o.oven_number === String(index + 1));
          return ovenData ? {
            initial_reading: ovenData.initial_reading || "",
            final_reading: ovenData.final_reading || "",
            m3_consumed: ovenData.m3_consumed || "",
            liters_consumed: ovenData.liters_consumed || "",
            total_cost: ovenData.total_cost || "",
          } : getInitialOvenData();
        });

        setFormData({
          entered_date_time: record.entered_date_time 
            ? convertToUserTimezone(record.entered_date_time,"YYYY-MM-DDTHH:mm") 
            : getCurrentDate("YYYY-MM-DDTHH:mm"),
          fc: record.fc || FC,
          price_per_l: record.price_per_l || PRICE_PER_L,
          ovens: mappedOvens,
        });
      } else {
        setFormData(getInitialFormData());
      }
    }
  }, [show, record, mode]);

  const handleClose = () => {
    setShow(false);
    setFormData(getInitialFormData());
  };

  const handleGlobalChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOvenChange = (ovenIndex, field, value) => {
    const updatedOvens = [...formData.ovens];
    updatedOvens[ovenIndex] = {
      ...updatedOvens[ovenIndex],
      [field]: value,
    };

    // Auto-calculate if initial_reading or final_reading changes
    if (field === "initial_reading" || field === "final_reading") {
      const initial = parseFloat(updatedOvens[ovenIndex].initial_reading) || 0;
      const final = parseFloat(updatedOvens[ovenIndex].final_reading) || 0;
      const fc = parseFloat(formData.fc) || 0;
      const price = parseFloat(formData.price_per_l) || 0;

      const m3 = final - initial;
      const liters = m3 * fc;
      const cost = liters * price;

      updatedOvens[ovenIndex].m3_consumed = m3 > 0 ? m3.toFixed(2) : "";
      updatedOvens[ovenIndex].liters_consumed = liters > 0 ? liters.toFixed(2) : "";
      updatedOvens[ovenIndex].total_cost = cost > 0 ? cost.toFixed(2) : "";
    }

    setFormData((prev) => ({ ...prev, ovens: updatedOvens }));
  };

  const handleSubmit = async () => {
    if (isCreating || isUpdating) return;

    // Prepare all 6 ovens data (including empty ones)
    const ovensData = formData.ovens.map((oven, index) => ({
      oven_number: (index + 1).toString(),
      initial_reading: oven.initial_reading || "0",
      final_reading: oven.final_reading || "0",
      m3_consumed: oven.m3_consumed || "0",
      liters_consumed: oven.liters_consumed || "0",
      total_cost: oven.total_cost || "0",
    }));

    // Create payload with all ovens and group_id for edit mode
    const payload = {
      entered_date_time: formData.entered_date_time,
      fc: formData.fc,
      price_per_l: formData.price_per_l,
      ovens: ovensData,
      ...(mode === "edit" && record?.group_id && { group_id: record.group_id }),
    };

    console.log("Payload being sent:", payload);

    try {
      if (mode === "edit" && record?.group_id) {
        await updateGasConsumptionRecord(payload).unwrap();
      } else {
        await createGasConsumptionRecord(payload).unwrap();
      }
      handleClose();
    } catch (error) {
      console.error("Error saving gas consumption records:", error);
    }
  };

  return (
    <CommonModal
    
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={mode === "edit" 
        ? _t("edit_gas_consumption_data", "Edit Gas Consumption Data") 
        : _t("add_gas_consumption_data", "Add Gas Consumption Data")
      }
      children={formData}
    >
      <Row className="">
        <Col lg={4}>
          <InputWithLabel
            label={_t("date", "Date")}
            type="datetime-local"
            name="entered_date_time"
            value={formData.entered_date_time}
            onChange={handleGlobalChange}
            required
          />
        </Col>
        <Col lg={4}>
          <InputWithLabel
            label={_t("fc", "Factor Constant (FC)")}
            type="number"
            name="fc"
            value={formData.fc}
            onChange={handleGlobalChange}
            required
          />
        </Col>
        <Col lg={4}>
          <InputWithLabel
            label={_t("price_per_l", "Price Per Liter")}
            type="number"
            name="price_per_l"
            value={formData.price_per_l}
            onChange={handleGlobalChange}
            required
          />
        </Col>
      </Row>

      <div className="modalScroll">
        <Table bordered hover responsive>
          <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
            <tr>
              <th>{_t("oven", "Oven")}</th>
              <th>{_t("initial_reading", "Initial Reading")}</th>
              <th>{_t("final_reading", "Final Reading")}</th>
              <th>{_t("m3_consumed", "M³ Consumed")}</th>
              <th>{_t("liters_consumed", "Liters Consumed")}</th>
              <th>{_t("total_cost", "Total Cost")}</th>
            </tr>
          </thead>
          <tbody>
            {formData.ovens.map((oven, index) => (
              <tr key={index}>
                <td className="text-nowrap" style={{ verticalAlign: "middle", fontWeight: "600" }}>
                  {index === 6 ? _t("evaporator", "Evaporator") : `${_t("oven", "Oven")} ${index + 1}`}
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={oven.initial_reading}
                    onChange={(e) =>
                      handleOvenChange(index, "initial_reading", e.target.value)
                    }
                    placeholder={"0"}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={oven.final_reading}
                    onChange={(e) =>
                      handleOvenChange(index, "final_reading", e.target.value)
                    }
                    placeholder={"0"}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={oven.m3_consumed}
                    disabled
                    style={{ background: "#f5f5f5" }}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={oven.liters_consumed}
                    disabled
                    style={{ background: "#f5f5f5" }}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={oven.total_cost}
                    disabled
                    style={{ background: "#f5f5f5" }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Row className="mt-3">
        <Col className="text-end">
          <Button
            label={mode === "edit" ? _t("update", "Update") : _t("save_changes", "Save Changes")}
            size="small"
            onClick={handleSubmit}
            disabled={isCreating || isUpdating}
          />
        </Col>
      </Row>
    </CommonModal>
  );
};

export default GasConsumptionModal;
